import { Popover2InteractionKind } from '@blueprintjs/popover2';
import { DocumentBlank, TrashCan, View } from '@carbon/icons-react';
import * as go from 'gojs';
import * as React from 'react';
import { useRouteMatch } from 'react-router-dom';
import {
  Body,
  MenuItem,
  MenuItems,
  OutlinedIcon,
  OutlinedIconButton,
  Tooltip,
} from 'vet-bones/design-system/components';

import { SidebarCard } from 'src/components/sidebarCard/SidebarCard';
import { ROUTE } from 'src/constants/routes';
import {
  TEST_IDS,
  getProjectResourceCardTestId,
  getProjectResourceMenuButtonTestId,
} from 'src/constants/testIds';
import { useRemoveResource } from 'src/containers/ProjectDiagram/hooks/remove/useRemoveResource';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { commitDiagramWhileSkippingUndoManager } from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { getResourceKey } from 'src/containers/ProjectDiagram/utils/resource/createResource';
import { setDiagramSelection } from 'src/containers/ProjectDiagram/utils/setDiagramSelection';
import { projectFileUploadSelector } from 'src/selectors/projectFileUpload';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

import cardStyles from 'src/components/sidebarCard/sidebarCard.module.scss';
import sidebarStyles from 'src/containers/ProjectSidebar/projectSidebar.module.scss';

const useProjectFileUpload = (fileId: string) => {
  const { projectId } = useRouterParams();
  return useReduxSelector(projectFileUploadSelector(projectId, fileId));
};

type ProjectSidebarFileUploadCardProps = {
  idx: number | undefined;
  fileId: string;
};

export const ProjectSidebarFileUploadCard: React.VFC<
  ProjectSidebarFileUploadCardProps
> = ({ idx, fileId }) => {
  const diagram = useDiagram();
  const dispatch = useReduxDispatch();
  const isMappingRoute = Boolean(useRouteMatch(ROUTE.PROJECT_MAPPING));

  const fileUpload = useProjectFileUpload(fileId);
  const handleDeleteFileUpload = useRemoveResource();

  const onClick = React.useCallback(() => {
    if (!diagram) {
      return;
    }

    const keysToSelect: go.Set<go.Part> = new go.Set();
    diagram.nodes.each((item) => {
      if (item.data.value === fileId) {
        keysToSelect.add(item);
      }
    });

    commitDiagramWhileSkippingUndoManager(diagram, (_, diagram) =>
      setDiagramSelection(keysToSelect)(diagram)
    );
  }, [diagram, fileId]);

  return (
    <SidebarCard
      actions={
        <Tooltip
          description={copy.components.project.resources.viewPreview}
          interactionKind={Popover2InteractionKind.HOVER_TARGET_ONLY}
          targetProps={{ className: cardStyles.sidebarCardMenu }}
        >
          <OutlinedIconButton
            leftIcon={View}
            onClick={() => {
              dispatch(
                uiSlice.actions.toggledFileUploadPreviewDialog({
                  isOpen: true,
                  resourceId: fileId,
                })
              );
            }}
            testId={TEST_IDS.projectSidebar.resourceMenu.viewPreviewButton}
          />
        </Tooltip>
      }
      menu={
        <MenuItems className={sidebarStyles.projectSidebarSmallMenuItems}>
          <MenuItem
            leftIcon={TrashCan}
            onClick={() =>
              handleDeleteFileUpload({ key: getResourceKey(fileId) })
            }
            testId={TEST_IDS.projectSidebar.resourceMenu.deleteButton}
            text={copy.components.project.resources.delete}
          />
        </MenuItems>
      }
      menuTestId={getProjectResourceMenuButtonTestId(fileId, idx)}
      onClick={isMappingRoute ? undefined : onClick}
      testId={getProjectResourceCardTestId(fileId, idx)}
      title={
        <>
          <OutlinedIcon
            IconComponent={DocumentBlank}
            testId={TEST_IDS.projectSidebar.resourceCard.fileUploadIcon}
          />
          <Body
            ellipsize
            size="md"
            testId={TEST_IDS.projectSidebar.resourceCard.title}
          >
            {fileUpload?.name || fileId}
          </Body>
        </>
      }
    />
  );
};
